import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { usePeptides } from '../context/PeptidesContext.jsx'
import { DISCLAIMER } from '../data/peptides.js'
import Seo from '../components/Seo.jsx'

const SECTIONS = [
  { type: 'peptide', title: 'Peptides', blurb: 'Peptide compounds and their research profiles.' },
  { type: 'amino', title: 'Aminos & Injectables', blurb: 'Amino acids, vitamins and related injectables.' },
]

function matches(p, q) {
  if (!q) return true
  const hay = [p.name, p.category, p.summary, ...(p.aka || []), ...(p.tags || [])]
    .join(' ')
    .toLowerCase()
  return hay.includes(q)
}

export default function Peptides() {
  const { peptides, loading, isAdmin } = usePeptides()
  const [query, setQuery] = useState('')

  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => peptides.filter((p) => matches(p, q)), [peptides, q])
  const total = filtered.length

  return (
    <main className="page">
      <Seo
        title="Peptide Education Library | CalculateMyPeptides"
        description="Plain-language educational profiles for popular peptides — BPC-157, TB-500, GHK-Cu, Ipamorelin, MOTS-c, PT-141 and more. Mechanism, uses, and dosing context."
        image="/og/peptide.png"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Peptides', path: '/peptides' }]}
      />
      <div className="container">
        <div className="between wrap" style={{ alignItems: 'flex-start' }}>
          <div>
            <div style={{ marginBottom: 8 }}>
              <span className="pill blue">Learn</span>
            </div>
            <h1 className="section-title">Peptide Education</h1>
            <p className="section-sub" style={{ maxWidth: '58ch' }}>
              Plain-language reference profiles for commonly discussed research peptides and injectables —
              what they are, how they’re thought to work, and the figures you’ll see cited.
            </p>
            <p className="soft" style={{ maxWidth: '68ch', marginTop: 10 }}>
              Each profile summarizes a peptide’s background, proposed mechanism, researched uses, a commonly
              cited protocol, and reconstitution notes, followed by a short FAQ. Every page also links to our
              free <Link to="/calculator">reconstitution calculator</Link>, pre-filled for that compound, so you
              can turn a vial size and target dose into the exact insulin-syringe units to draw. New to the
              terminology? The <Link to="/glossary">peptide glossary</Link> defines the reconstitution and dosing
              terms you’ll see. This library is educational writing for general understanding — it is not medical
              advice, and none of these compounds are approved for the uses discussed. Always work with a qualified
              healthcare provider.
            </p>
          </div>
          {isAdmin && <Link to="/admin/peptides" className="btn btn-ghost btn-sm">⚙️ Manage</Link>}
        </div>

        {/* search */}
        <div className="lib-search">
          <span className="lib-search-icon">🔍</span>
          <input
            className="input"
            type="search"
            placeholder="Search by name, alias, tag or category…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search the library"
          />
          {query && <button className="lib-search-clear" onClick={() => setQuery('')} aria-label="Clear">✕</button>}
        </div>

        {loading ? (
          <div className="card empty"><div className="spinner" /><p style={{ marginTop: 12 }}>Loading library…</p></div>
        ) : total === 0 ? (
          <div className="card empty">
            <div className="emoji">🔍</div>
            <h3 style={{ marginBottom: 6 }}>No matches for “{query}”</h3>
            <p style={{ marginBottom: 0 }}>Try a different name, alias, or tag.</p>
          </div>
        ) : (
          SECTIONS.map((s) => {
            const items = filtered.filter((p) => (p.type || 'peptide') === s.type)
            if (items.length === 0) return null
            return (
              <section key={s.type} className="lib-section">
                <div className="lib-section-head">
                  <h2 className="lib-section-title">{s.title}</h2>
                  <span className="muted small">{items.length}</span>
                </div>
                <div className="pep-grid">
                  {items.map((p) => (
                    <Link key={p.slug} to={`/peptides/${p.slug}`} className="card pep-card fade-in">
                      <div className="pep-card-top">
                        <span className="pep-cat">{p.category}</span>
                        <h3>{p.name}</h3>
                        <p className="pep-summary">{p.summary}</p>
                      </div>
                      <div className="pep-tags">
                        {(p.tags || []).slice(0, 3).map((t) => (
                          <button
                            key={t}
                            type="button"
                            className="pill pill-tag"
                            title={`Find ${t}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuery(t) }}
                          >
                            {t}
                          </button>
                        ))}
                        <span className="pep-arrow">Read →</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )
          })
        )}

        <p className="muted small" style={{ marginTop: 26, maxWidth: '70ch' }}>⚠️ {DISCLAIMER}</p>
      </div>
    </main>
  )
}
